import { useEffect, useRef, useState } from 'react';
import '../styles/admin.css';

const formularioInicial = {
  idprd: '',
  desc: '',
  precio: '',
  rating: '',
  vendidos: '',
  imageUrl: '',
  url: '',
};

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://serverjsondrive.onrender.com').replace(/\/$/, '');

function normalizarTexto(valor) {
  return String(valor ?? '').toLowerCase();
}

function construirUrl(path) {
  return API_BASE_URL ? `${API_BASE_URL}${path}` : path;
}

export default function AdminPanel() {
  const [productos, setProductos] = useState([]);
  const [estado, setEstado] = useState('cargando');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [modoFormulario, setModoFormulario] = useState('crear');
  const [formulario, setFormulario] = useState(formularioInicial);
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('recientes');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(12);
  const [pestanaActiva, setPestanaActiva] = useState('datos');
  const [authStatus, setAuthStatus] = useState('checking');
  const [authUser, setAuthUser] = useState(null);
  const [credenciales, setCredenciales] = useState({ username: 'admin', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const editorRef = useRef(null);
  const busquedaDiferida = busqueda;

  async function requestJson(path, options = {}) {
    const headers = options.body
      ? { 'Content-Type': 'application/json', ...(options.headers ?? {}) }
      : { ...(options.headers ?? {}) };

    const response = await fetch(construirUrl(path), {
      ...options,
      headers,
      credentials: 'include',
    });

    let data = {};

    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (response.status === 401) {
      setAuthStatus('guest');
      setAuthUser(null);
      throw new Error(data.error ?? 'Sesion no valida');
    }

    if (!response.ok) {
      throw new Error(data.detalle ?? data.error ?? 'No se pudo completar la solicitud');
    }

    return data;
  }

  async function cargarProductos() {
    setEstado('cargando');
    setError('');

    try {
      const data = await requestJson('/api/productos');
      setProductos(Array.isArray(data) ? data : []);
      setEstado('listo');
    } catch (requestError) {
      setError(requestError.message);
      setEstado('error');
    }
  }

  async function verificarSesion() {
    try {
      const data = await requestJson('/api/auth/me');

      if (data.authenticated) {
        setAuthUser(data.user);
        setAuthStatus('authenticated');
        await cargarProductos();
        return;
      }

      setAuthStatus('guest');
    } catch {
      setAuthStatus('guest');
    }
  }

  useEffect(() => {
    verificarSesion();
  }, []);

  useEffect(() => {
    setPagina(1);
  }, [busquedaDiferida, orden, porPagina]);

  function actualizarCampo(event) {
    const { name, value } = event.target;
    setFormulario((actual) => ({
      ...actual,
      [name]: value,
    }));
  }

  function actualizarCredenciales(event) {
    const { name, value } = event.target;
    setCredenciales((actual) => ({
      ...actual,
      [name]: value,
    }));
  }

  function resetFormulario() {
    setFormulario(formularioInicial);
    setModoFormulario('crear');
    setPestanaActiva('datos');
  }

  function editarProducto(producto) {
    setFormulario({
      idprd: String(producto.idprd ?? ''),
      desc: producto.desc ?? '',
      precio: producto.precio ?? '',
      rating: producto.rating ?? '',
      vendidos: producto.vendidos ?? '',
      imageUrl: producto.imageUrl ?? '',
      url: producto.url ?? '',
    });
    setModoFormulario('editar');
    setPestanaActiva('datos');
    editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function iniciarSesion(event) {
    event.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const data = await requestJson('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(credenciales),
      });

      setAuthUser(data.user);
      setAuthStatus('authenticated');
      await cargarProductos();
    } catch (requestError) {
      setLoginError(requestError.message);
    } finally {
      setLoginLoading(false);
    }
  }

  async function cerrarSesion() {
    try {
      await requestJson('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({}),
      });
    } catch {
    } finally {
      setAuthStatus('guest');
      setAuthUser(null);
      setProductos([]);
      setCredenciales((actual) => ({ ...actual, password: '' }));
    }
  }

  async function guardarProducto(event) {
    event.preventDefault();
    setGuardando(true);
    setError('');

    try {
      const payload = {
        ...formulario,
        idprd: formulario.idprd.trim(),
      };

      const endpoint =
        modoFormulario === 'crear'
          ? '/api/productos'
          : `/api/productos/${formulario.idprd}`;

      await requestJson(endpoint, {
        method: modoFormulario === 'crear' ? 'POST' : 'PUT',
        body: JSON.stringify(payload),
      });

      resetFormulario();
      await cargarProductos();
    } catch (requestError) {
      setError(requestError.message);
      setEstado('error');
    } finally {
      setGuardando(false);
    }
  }

  async function borrarProducto(idprd) {
    const confirmado = window.confirm(`Se eliminara el producto ${idprd}. Continuar?`);

    if (!confirmado) {
      return;
    }

    setGuardando(true);
    setError('');

    try {
      await requestJson(`/api/productos/${idprd}`, {
        method: 'DELETE',
      });

      if (String(formulario.idprd) === String(idprd)) {
        resetFormulario();
      }

      await cargarProductos();
    } catch (requestError) {
      setError(requestError.message);
      setEstado('error');
    } finally {
      setGuardando(false);
    }
  }

  const productosFiltrados = [...productos]
    .filter((producto) => {
      const texto = busquedaDiferida.trim().toLowerCase();

      if (!texto) {
        return true;
      }

      return [
        producto.idprd,
        producto.desc,
        producto.precio,
        producto.rating,
        producto.vendidos,
      ].some((campo) => normalizarTexto(campo).includes(texto));
    })
    .sort((a, b) => {
      const idA = Number(a.idprd) || 0;
      const idB = Number(b.idprd) || 0;
      const descA = normalizarTexto(a.desc);
      const descB = normalizarTexto(b.desc);

      if (orden === 'id-asc') {
        return idA - idB;
      }

      if (orden === 'id-desc') {
        return idB - idA;
      }

      if (orden === 'desc-asc') {
        return descA.localeCompare(descB);
      }

      return idB - idA;
    });

  const totalPaginas = Math.max(1, Math.ceil(productosFiltrados.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const inicio = (paginaActual - 1) * porPagina;
  const fin = inicio + porPagina;
  const productosVisibles = productosFiltrados.slice(inicio, fin);
  const resultadosBusqueda = busquedaDiferida.trim() ? productosFiltrados.slice(0, 6) : [];

  if (authStatus === 'checking') {
    return (
      <main className="admin-shell admin-auth-shell">
        <section className="admin-login-panel admin-centered-panel">
          <p className="admin-eyebrow">Verificando sesion</p>
          <h1>Preparando acceso</h1>
          <p className="admin-hero-copy">Estamos comprobando si ya existe una sesion activa.</p>
        </section>
      </main>
    );
  }

  if (authStatus !== 'authenticated') {
    return (
      <main className="admin-shell admin-auth-shell">
        <section className="admin-login-panel admin-centered-panel">
          <p className="admin-eyebrow">Acceso administrador</p>
          <h1>Iniciar sesion</h1>
          <p className="admin-hero-copy">
            Solo un administrador autenticado puede entrar al panel y modificar productos.
          </p>

          <form className="admin-login-form" onSubmit={iniciarSesion}>
            <label>
              Usuario
              <input name="username" value={credenciales.username} onChange={actualizarCredenciales} />
            </label>
            <label>
              Contraseña
              <input type="password" name="password" value={credenciales.password} onChange={actualizarCredenciales} />
            </label>
            {loginError && <p className="admin-error-text">{loginError}</p>}
            <button type="submit" disabled={loginLoading}>
              {loginLoading ? 'Ingresando...' : 'Entrar como admin'}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-shell">
      <section className="admin-hero-panel admin-full-width-hero">
        <div className="admin-hero-copy-block">
          <p className="admin-eyebrow">Panel de administracion</p>
          <h1>Administración de productos</h1>
          <p className="admin-hero-copy">
            Gestiona el catálogo, busca elementos, edítalos rápidamente y deja preparado este espacio para futuras opciones como cierre de sesión y seguridad.
          </p>
        </div>
        <div className="admin-hero-side-actions">
          <div className="admin-hero-badges">
            <span className="admin-badge-chip">Admin activo</span>
            <span className="admin-badge-chip admin-soft">{authUser?.username}</span>
          </div>
          <button type="button" className="secondary admin-ghost-control" onClick={cerrarSesion}>
            Logout
          </button>
        </div>
      </section>

      <section className="admin-main-layout" ref={editorRef}>
        <section className="admin-editor-panel">
          <div className="admin-editor-topbar">
            <div>
              <p className="admin-eyebrow">Editor</p>
              <h2>{modoFormulario === 'crear' ? 'Nuevo producto' : 'Editar producto'}</h2>
            </div>
            {modoFormulario === 'editar' && <span className="admin-editing-pill">ID #{formulario.idprd}</span>}
          </div>

          <div className="admin-editor-tabs" role="tablist" aria-label="Secciones del editor">
            <button type="button" className={`admin-tab-button${pestanaActiva === 'datos' ? ' active' : ''}`} onClick={() => setPestanaActiva('datos')}>
              Datos
            </button>
            <button type="button" className={`admin-tab-button${pestanaActiva === 'imagen' ? ' active' : ''}`} onClick={() => setPestanaActiva('imagen')}>
              Imagen
            </button>
            <button type="button" className={`admin-tab-button${pestanaActiva === 'enlace' ? ' active' : ''}`} onClick={() => setPestanaActiva('enlace')}>
              Enlace
            </button>
          </div>

          <form className="admin-tabbed-form" onSubmit={guardarProducto}>
            {pestanaActiva === 'datos' && (
              <section className="admin-side-tab-panel">
                <div className="admin-side-tab-grid">
                  <label>
                    ID
                    <input name="idprd" value={formulario.idprd} onChange={actualizarCampo} placeholder="Automatico si lo dejas vacio" disabled={modoFormulario === 'editar'} />
                  </label>
                  <label>
                    Precio
                    <input name="precio" value={formulario.precio} onChange={actualizarCampo} />
                  </label>
                  <label>
                    Rating
                    <input name="rating" value={formulario.rating} onChange={actualizarCampo} />
                  </label>
                  <label>
                    Vendidos
                    <input name="vendidos" value={formulario.vendidos} onChange={actualizarCampo} />
                  </label>
                  <label className="admin-full-width">
                    Descripcion
                    <textarea name="desc" value={formulario.desc} onChange={actualizarCampo} rows="5" required />
                  </label>
                </div>
              </section>
            )}

            {pestanaActiva === 'imagen' && (
              <section className="admin-side-tab-panel">
                <div className="admin-side-tab-grid">
                  <label className="admin-full-width">
                    Image URL
                    <input name="imageUrl" value={formulario.imageUrl} onChange={actualizarCampo} />
                  </label>
                  <div className="admin-preview-card admin-full-width">
                    <div>
                      <p className="admin-label">Vista previa</p>
                      <strong>{formulario.desc || 'Sin descripcion'}</strong>
                      <p className="admin-preview-meta">{formulario.precio || 'Sin precio'} · {formulario.rating || 'Sin rating'}</p>
                    </div>
                    {formulario.imageUrl ? <img src={formulario.imageUrl} alt="Vista previa del producto" /> : <div className="admin-preview-placeholder">Sin imagen</div>}
                  </div>
                </div>
              </section>
            )}

            {pestanaActiva === 'enlace' && (
              <section className="admin-side-tab-panel">
                <div className="admin-side-tab-grid">
                  <label className="admin-full-width">
                    URL
                    <input name="url" value={formulario.url} onChange={actualizarCampo} />
                  </label>
                </div>
              </section>
            )}

            <div className="admin-form-actions">
              <button type="submit" disabled={guardando}>
                {guardando ? 'Guardando...' : modoFormulario === 'crear' ? 'Agregar producto' : 'Guardar cambios'}
              </button>
              <button type="button" className="secondary" onClick={resetFormulario} disabled={guardando}>
                Limpiar
              </button>
            </div>
          </form>
        </section>

        <section className="admin-catalog-column">
          <section className="admin-status-panel">
            <div className="admin-status-card admin-accent">
              <p className="admin-label">Estado</p>
              <strong>{guardando ? 'guardando' : estado}</strong>
            </div>
            <div className="admin-status-card">
              <p className="admin-label">Total</p>
              <strong>{productos.length}</strong>
            </div>
            <div className="admin-status-card">
              <p className="admin-label">Mostrando</p>
              <strong>{productosVisibles.length}</strong>
            </div>
            <div className="admin-status-card">
              <p className="admin-label">Pagina</p>
              <strong>{paginaActual} / {totalPaginas}</strong>
            </div>
          </section>

          <section className="admin-list-panel">
            <div className="admin-section-heading">
              <div>
                <p className="admin-eyebrow">Productos</p>
                <h2>Listado cargado</h2>
              </div>
              <button type="button" className="secondary" onClick={cargarProductos} disabled={guardando}>
                Recargar
              </button>
            </div>

            <section className="admin-toolbar-panel">
              <label className="admin-toolbar-field">
                <span>Buscar</span>
                <input value={busqueda} onChange={(event) => setBusqueda(event.target.value)} placeholder="ID, descripcion, precio..." />
              </label>
              <label className="admin-toolbar-field">
                <span>Ordenar</span>
                <select value={orden} onChange={(event) => setOrden(event.target.value)}>
                  <option value="recientes">Mas recientes</option>
                  <option value="id-asc">ID ascendente</option>
                  <option value="id-desc">ID descendente</option>
                  <option value="desc-asc">Descripcion A-Z</option>
                </select>
              </label>
              <label className="admin-toolbar-field">
                <span>Por pagina</span>
                <select value={porPagina} onChange={(event) => setPorPagina(Number(event.target.value))}>
                  <option value="8">8</option>
                  <option value="12">12</option>
                  <option value="24">24</option>
                  <option value="48">48</option>
                </select>
              </label>
            </section>

            {resultadosBusqueda.length > 0 && (
              <section className="admin-quick-results">
                <div className="admin-quick-results-head">
                  <p className="admin-eyebrow">Resultados rapidos</p>
                  <span>{resultadosBusqueda.length} seleccionables</span>
                </div>
                <div className="admin-quick-results-list">
                  {resultadosBusqueda.map((producto) => (
                    <button key={`quick-${producto.idprd}`} type="button" className="admin-quick-result-item" onClick={() => editarProducto(producto)}>
                      <span>#{producto.idprd}</span>
                      <strong>{producto.desc}</strong>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {error && <p className="admin-error-text">{error}</p>}
            {estado === 'cargando' && <p className="admin-empty-state">Cargando datos desde el backend...</p>}
            {estado === 'listo' && productosFiltrados.length === 0 && <p className="admin-empty-state">No hay resultados para esa busqueda.</p>}

            {productosVisibles.length > 0 && (
              <div className="admin-cards-grid">
                {productosVisibles.map((producto, index) => (
                  <article className="admin-product-card" key={producto.idprd ?? index}>
                    <div className="admin-card-topline">
                      <span className="admin-card-id">#{producto.idprd ?? '-'}</span>
                      <span className="admin-rating-pill">{producto.rating || 'Sin rating'}</span>
                    </div>
                    <button type="button" className="admin-card-select" onClick={() => editarProducto(producto)}>
                      <p className="admin-card-title">{producto.desc ?? '-'}</p>
                    </button>
                    <dl className="admin-product-meta">
                      <div>
                        <dt>Precio</dt>
                        <dd>{producto.precio ?? '-'}</dd>
                      </div>
                      <div>
                        <dt>Vendidos</dt>
                        <dd>{producto.vendidos ?? '-'}</dd>
                      </div>
                    </dl>
                    <div className="admin-card-actions">
                      <button type="button" className="secondary" onClick={() => editarProducto(producto)}>
                        Editar
                      </button>
                      {producto.url ? <a className="admin-ghost-link" href={producto.url} target="_blank" rel="noreferrer">Ver enlace</a> : <span className="admin-ghost-link admin-disabled">Sin enlace</span>}
                      <button type="button" className="danger" onClick={() => borrarProducto(producto.idprd)}>
                        Eliminar
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {productosFiltrados.length > 0 && (
              <section className="admin-pagination-bar">
                <p>
                  Mostrando <strong>{inicio + 1}</strong> a <strong>{Math.min(fin, productosFiltrados.length)}</strong> de <strong>{productosFiltrados.length}</strong>
                </p>
                <div className="admin-pagination-actions">
                  <button type="button" className="secondary" disabled={paginaActual <= 1} onClick={() => setPagina((actual) => Math.max(1, actual - 1))}>
                    Anterior
                  </button>
                  <button type="button" className="secondary" disabled={paginaActual >= totalPaginas} onClick={() => setPagina((actual) => Math.min(totalPaginas, actual + 1))}>
                    Siguiente
                  </button>
                </div>
              </section>
            )}
          </section>
        </section>
      </section>
    </main>
  );
}
